import { useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import HighlightOffOutlined from '@mui/icons-material/HighlightOffOutlined';
import RouteOutlined from '@mui/icons-material/RouteOutlined';
import { DataTable } from '../components/DataTable';
import { CustomerFinancials } from '../features/finance/FinancialsTabs';
import { EditRecordButton } from '../components/EditRecordButton';
import { ProfileHeader } from '../components/ProfileHeader';
import { ViewCommunicationsButton } from '../features/communications/ViewCommunicationsButton';
import { Panel } from '../components/Panel';
import { VaultNotes } from '../components/VaultNotes';
import { StatTile } from '../components/StatTile';
import { TileGrid } from '../components/TileGrid';
import { StatusChip } from '../components/StatusChip';
import { WithSideColumn } from '../components/WithSideColumn';
import { InfoPanel, RecentActivityPanel, StatusDonutPanel, SummaryPanel } from '../features/customers/CustomerPanels';
import { useCustomerStats } from '../features/customers/stats';
import { pct } from '../lib/numbers';
import { useCustomers } from '../features/customers/useCustomers';
import { QUOTE_TONE, type Quotation } from '../features/quotations/sample';
import { ActiveShipmentsTile } from '../features/shipments/ActiveShipmentsTile';
import { ConversationLinksPanel, ShipmentsTabPanel } from '../features/shipments/ShipmentLinkPanels';
import { money } from '../lib/money';

const TABS = ['Overview', 'Shipments', 'Quotations', 'Communications', 'Financials', 'Activity History'] as const;
const GAP = { xs: 1.5, md: 2 };

/** One customer: who they are, how their shipments are going, and what is waiting */
export function CustomerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const customers = useCustomers();
  const statsOf = useCustomerStats();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Overview');
  const c = customers.items.find((x) => x.id === id);
  if (customers.loading) return null; // the list is still loading: do not decide "not found" yet
  if (!c) return <Navigate to="/customers" replace />;
  const st = statsOf(c);
  const go = (to: string) => () => void navigate(to);

  return (
    <Stack spacing={GAP}>
      <ProfileHeader
        back={{ to: '/customers', label: 'Customers' }}
        name={c.name}
        status={{ label: `${c.status} Customer`, tone: c.status === 'Active' ? 'success' : 'neutral' }}
        details={[c.industry, [c.city, c.country].filter(Boolean).join(', '), c.phone, c.email, c.website]}
        tabs={TABS}
        tab={tab}
        onTab={setTab}
        actions={
          <>
            <Button variant="contained" onClick={go('/shipments/new')}>
              New Shipment
            </Button>
            <Button variant="contained" onClick={go('/quotations/new')}>
              New Quotation
            </Button>
            <ViewCommunicationsButton srb={st.ships[0]?.srb} />
            <EditRecordButton what="customer" />
          </>
        }
      />

      {tab === 'Overview' && (
        <WithSideColumn
          side={
            <>
              <StatusDonutPanel st={st} />
              <RecentActivityPanel st={st} />
            </>
          }
        >
          <TileGrid>
            <StatTile
              count={st.total}
              title="Total Shipments"
              note="All time"
              icon={<RouteOutlined />}
              tone="neutral"
              onClick={() => setTab('Shipments')}
            />
            <ActiveShipmentsTile count={st.active} onClick={() => setTab('Shipments')} />
            <StatTile
              count={st.arrived}
              title={`Arrived (${pct(st.arrived, st.total)}%)`}
              note="Successfully delivered"
              icon={<CheckCircleOutlined />}
              tone="success"
              onClick={() => setTab('Shipments')}
            />
            <StatTile
              count={st.lost}
              title={`Lost (${pct(st.lost, st.total)}%)`}
              note="Did not proceed"
              icon={<HighlightOffOutlined />}
              tone="error"
              onClick={() => setTab('Quotations')}
            />
          </TileGrid>
          <Box
            sx={{
              display: 'grid',
              gap: GAP,
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
              alignItems: 'start',
            }}
          >
            <InfoPanel c={c} />
            <SummaryPanel c={c} st={st} />
          </Box>
          <VaultNotes notes={c.notes} />
        </WithSideColumn>
      )}
      {tab === 'Shipments' && <ShipmentsTabPanel rows={st.ships} />}
      {tab === 'Quotations' && (
        <Panel title="Quotations">
          <DataTable<Quotation>
            rows={st.quotes}
            rowKey={(q) => q.number + q.srb}
            empty="No quotations yet."
            onRowClick={(q) => void navigate(`/quotations/${q.srb}`)}
            columns={[
              { header: 'SRB', primary: true, nowrap: true, render: (q) => <b>{q.srb}</b> },
              { header: 'Quotation No.', nowrap: true, render: (q) => q.number },
              { header: 'Quoted Price', nowrap: true, render: (q) => money(q.price, q.currency) },
              { header: 'Valid Until', nowrap: true, render: (q) => q.validUntil },
              {
                header: 'Status',
                nowrap: true,
                action: true,
                render: (q) => <StatusChip label={q.status} tone={QUOTE_TONE[q.status]} />,
              },
            ]}
          />
        </Panel>
      )}
      {tab === 'Communications' && <ConversationLinksPanel ships={st.ships} />}
      {tab === 'Activity History' && <RecentActivityPanel st={st} />}
      {tab === 'Financials' && <CustomerFinancials customer={c.name} />}
    </Stack>
  );
}
